// Auto-generated by cli-contracts. Do not edit.
import { Command } from "commander";
import { CONTRACT_YAML, CONTRACT_JSON_STR } from "./contract.js";

export interface CommandHandlers {
  init: (options: { adapter?: string; output?: string; force?: boolean; showPrompt?: boolean }, parentOpts: Record<string, unknown>) => Promise<void | string>;
  generate: (options: { clean?: boolean; check?: boolean; templates?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
  run: (workflowId: string | undefined, userRequest: string | undefined, options: { adapter?: string; maxRetries?: string; maxFollowUps?: string; dryRun?: boolean; autoApproveGates?: boolean; file?: string; logFile?: string; showPrompt?: boolean }, parentOpts: Record<string, unknown>) => Promise<void | string>;
  list: (resource: string | undefined, options: { format?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
  showPrompt: (taskId: string | undefined, options: { userRequest?: string; format?: string; withPlugins?: boolean }, parentOpts: Record<string, unknown>) => Promise<void>;
  implement: (description: string | undefined, options: { adapter?: string; projectDir?: string; dryRun?: boolean; model?: string; output?: string; reportFormat?: string; logFile?: string; showPrompt?: boolean }, parentOpts: Record<string, unknown>) => Promise<void | string>;
  auditImplementation: (projectDir: string | undefined, options: { adapter?: string; focus?: string; dryRun?: boolean; failOn?: string; model?: string; output?: string; reportFormat?: string; logFile?: string; showPrompt?: boolean }, parentOpts: Record<string, unknown>) => Promise<void | string>;
  agents: (options: { format?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
  doctor: (options: Record<string, never>, parentOpts: Record<string, unknown>) => Promise<void>;
  navigationIndex: (options: { format?: string; artifact?: string; output?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
}

export function createProgram(
  handlers: CommandHandlers,
  version: string,
): Command {
  const program = new Command();
  program
    .name("agent-runtime")
    .version(version, "-V, --version")
    .description("CLI for agent-contracts-runtime — generate, run, and manage agent workflows.");

  program.option("-c, --config <value>", "Path to agents.conf.yaml (project runtime config).", "./agents.conf.yaml");
  program.option("-v, --verbose", "Enable verbose output.", false);
  program.option("-q, --quiet", "Suppress non-error output.", false);

  program
    .command("init")
    .description("Initialize runtime scaffolding for a project.")
    .option("-a, --adapter <value>", "Default SDK adapter to configure.", "mock")
    .option("-o, --output <value>", "Output directory for generated scaffolding.", ".")
    .option("-f, --force", "Overwrite existing files without prompting.", false)
    .option("--show-prompt", "Output the constructed prompt without calling the LLM API.", false)
    .action(async (opts, cmd) => {
      if (opts.showPrompt) {
        const prompt = await handlers.init(opts, cmd.optsWithGlobals());
        if (typeof prompt === "string") process.stdout.write(prompt + "\n");
        return;
      }
      await handlers.init(opts, cmd.optsWithGlobals());
    });

  program
    .command("generate")
    .description("Generate runtime contracts and hooks from DSL.")
    .option("--clean", "Delete generated_dir before regenerating.\nRemoves stale files that may remain from previous generations.\n", false)
    .option("--check", "Dry-run mode for CI. Does not write files.\nCompares current generated output with what would be regenerated.\nExits with code 1 if any differences are found.\n", false)
    .option("-t, --templates <value>", "Custom templates directory. Overrides built-in templates.")
    .action(async (opts, cmd) => {
      await handlers.generate(opts, cmd.optsWithGlobals());
    });

  program
    .command("run")
    .description("Execute a workflow.")
    .argument("[workflow-id]", "ID of the workflow to execute. Required unless --file is used.")
    .argument("[user-request]", "Natural language description of what to do. Required unless --file is used.")
    .option("-a, --adapter <value>", "SDK adapter to use. Overrides config.")
    .option("-r, --max-retries <value>", "Override default max retries per step.")
    .option("-f, --max-follow-ups <value>", "Override default max follow-ups per step.")
    .option("-n, --dry-run", "Simulate execution without calling the SDK adapter.", false)
    .option("--auto-approve-gates", "Automatically approve all gate steps.", false)
    .option("--file <value>", "Path to a YAML invocation file. When provided, workflow-id and\nuser-request arguments are read from the file instead of CLI args.\n")
    .option("-l, --log-file <value>", "Write agent progress log to this file. Overrides config logging.progress_log.")
    .option("--show-prompt", "Output the constructed prompt without calling the LLM API.", false)
    .action(async (workflowId, userRequest, opts, cmd) => {
      if (opts.showPrompt) {
        const prompt = await handlers.run(workflowId, userRequest, opts, cmd.optsWithGlobals());
        if (typeof prompt === "string") process.stdout.write(prompt + "\n");
        return;
      }
      await handlers.run(workflowId, userRequest, opts, cmd.optsWithGlobals());
    });

  program
    .command("list")
    .description("List available workflows, tasks, or agents.")
    .argument("<resource>", "Type of resource to list.")
    .option("--format <value>", "Output format.", "table")
    .action(async (resource, opts, cmd) => {
      await handlers.list(resource, opts, cmd.optsWithGlobals());
    });

  program
    .command("show-prompt")
    .description("Display the generated prompt for a task.")
    .argument("<task-id>", "ID of the task to show the prompt for (e.g. plan-and-implement, audit-tests).")
    .option("-u, --user-request <value>", "User request text to include in the prompt preview.", "(preview)")
    .option("--format <value>", "Output format.", "markdown")
    .option("--with-plugins", "Apply registered plugin prompt enhancers to the output.", false)
    .action(async (taskId, opts, cmd) => {
      await handlers.showPrompt(taskId, opts, cmd.optsWithGlobals());
    });

  program
    .command("implement")
    .description("Implement LLM-powered commands in a CLI tool.")
    .argument("<description>", "Natural language description of the LLM feature(s) to implement. Examples: \"Add an audit command for migration safety\", \"Add propose and explain commands for SQL optimization\".")
    .option("-a, --adapter <value>", "SDK adapter to use. Must be an agentic adapter (claude).", "claude")
    .option("-p, --project-dir <value>", "Path to the target project directory.", ".")
    .option("-n, --dry-run", "Output the prompt context without calling the LLM.", false)
    .option("--model <value>", "Model name override for the selected adapter.")
    .option("-o, --output <value>", "Write the result to a file instead of stdout.")
    .option("--report-format <value>", "Output format for the result.", "text")
    .option("-l, --log-file <value>", "Write agent progress log to this file.")
    .option("--show-prompt", "Output the constructed prompt without calling the LLM API.", false)
    .action(async (description, opts, cmd) => {
      if (opts.showPrompt) {
        const prompt = await handlers.implement(description, opts, cmd.optsWithGlobals());
        if (typeof prompt === "string") process.stdout.write(prompt + "\n");
        return;
      }
      await handlers.implement(description, opts, cmd.optsWithGlobals());
    });

  program
    .command("audit-implementation")
    .description("Audit an LLM command integration for pattern conformance.")
    .argument("[project-dir]", "Path to the project directory to audit. Defaults to current directory.")
    .option("-a, --adapter <value>", "SDK adapter to use for the audit agent.", "claude")
    .option("--focus <value>", "Limit audit to a specific layer. Defaults to all layers.", "all")
    .option("-n, --dry-run", "Output the prompt context without calling the LLM.", false)
    .option("--fail-on <value>", "Minimum finding severity that causes a non-zero exit.", "error")
    .option("--model <value>", "Model name override for the selected adapter.")
    .option("-o, --output <value>", "Write the result to a file instead of stdout.")
    .option("--report-format <value>", "Output format for the audit report.", "text")
    .option("-l, --log-file <value>", "Write agent progress log to this file.")
    .option("--show-prompt", "Output the constructed prompt without calling the LLM API.", false)
    .action(async (projectDir, opts, cmd) => {
      if (opts.showPrompt) {
        const prompt = await handlers.auditImplementation(projectDir, opts, cmd.optsWithGlobals());
        if (typeof prompt === "string") process.stdout.write(prompt + "\n");
        return;
      }
      await handlers.auditImplementation(projectDir, opts, cmd.optsWithGlobals());
    });

  program
    .command("agents")
    .description("Output the full resolved DSL as structured data.")
    .option("-F, --format <value>", "Output format.", "yaml")
    .action(async (opts, cmd) => {
      await handlers.agents(opts, cmd.optsWithGlobals());
    });

  program
    .command("doctor")
    .description("Verify configuration, generated files, and SDK connectivity.")
    .action(async (opts, cmd) => {
      await handlers.doctor({}, cmd.optsWithGlobals());
    });

  program
    .command("navigation-index")
    .description("Build artifact navigation index from resolved DSL.")
    .option("-F, --format <value>", "Output format.", "json")
    .option("--artifact <value>", "Filter output to a single artifact by ID.")
    .option("-o, --output <value>", "Write output to a file instead of stdout.")
    .action(async (opts, cmd) => {
      await handlers.navigationIndex(opts, cmd.optsWithGlobals());
    });


  // Built-in extract command (auto-injected by cli-contracts)
  program
    .command("extract")
    .description("Extract contract specification for this CLI tool.")
    .argument("[commands...]", "Command IDs to extract. Use dot notation.")
    .option("-a, --all", "Extract all commands.", false)
    .option("--include-meta", "Include extraction metadata.", true)
    .option("-F, --format <format>", "Output format (yaml or json).", "yaml")
    .action(async (commands: string[], opts: { all?: boolean; includeMeta?: boolean; format?: string }, cmd: Command) => {
      if (commands.length === 0 && !opts.all) {
        process.stderr.write(JSON.stringify({ code: "INVALID_ARGS", message: "Specify command IDs or use --all" }) + "\n");
        process.exit(2);
      }

      const format = opts.format || "yaml";
      const doc = JSON.parse(CONTRACT_JSON_STR);
      const cmdIds = opts.all ? [] : commands;

      if (cmdIds.length === 0) {
        // --all: output full contract
        if (format === "json") {
          const out: Record<string, unknown> = {};
          if (opts.includeMeta) {
            out._meta = {
              source: "embedded",
              type: "cli-contracts/extract",
              extractedAt: new Date().toISOString(),
              specVersion: doc.cli_contracts ?? "0.1.0",
              commands: ["agent-runtime.init","agent-runtime.generate","agent-runtime.run","agent-runtime.list","agent-runtime.show-prompt","agent-runtime.implement","agent-runtime.audit-implementation","agent-runtime.agents","agent-runtime.doctor","agent-runtime.navigation-index"],
            };
          }
          Object.assign(out, doc);
          process.stdout.write(JSON.stringify(out, null, 2) + "\n");
        } else {
          // YAML output
          const yamlLines: string[] = [];
          yamlLines.push("# agent-runtime extract");
          yamlLines.push("# source: embedded");
          yamlLines.push("# type: cli-contracts/command-extract");
          if (opts.includeMeta) {
            yamlLines.push("---");
            yamlLines.push("source: embedded");
            yamlLines.push("type: cli-contracts/command-extract");
            yamlLines.push("extractedAt: " + new Date().toISOString());
            yamlLines.push("spec_version: " + (doc.cli_contracts ?? "0.1.0"));
            yamlLines.push("commands:");
            for (const id of ["agent-runtime.init","agent-runtime.generate","agent-runtime.run","agent-runtime.list","agent-runtime.show-prompt","agent-runtime.implement","agent-runtime.audit-implementation","agent-runtime.agents","agent-runtime.doctor","agent-runtime.navigation-index"]) {
              yamlLines.push("  - " + id);
            }
          }
          yamlLines.push("---");
          yamlLines.push(CONTRACT_YAML);
          process.stdout.write(yamlLines.join("\n") + "\n");
        }
      } else {
        // Filter specific commands
        const filtered: Record<string, unknown> = {
          cli_contracts: doc.cli_contracts,
          info: doc.info,
          command_sets: {},
        };
        const fcs = filtered.command_sets as Record<string, Record<string, unknown>>;
        for (const [setId, cs] of Object.entries(doc.command_sets ?? {})) {
          const cmds = (cs as Record<string, unknown>).commands as Record<string, unknown> | undefined;
          if (!cmds) continue;
          const matched: Record<string, unknown> = {};
          for (const [cmdId, cmdDef] of Object.entries(cmds)) {
            const fullId = setId + "." + cmdId;
            if (cmdIds.some((id) => id === cmdId || id === fullId || cmdId.startsWith(id + "."))) {
              matched[cmdId] = cmdDef;
            }
          }
          if (Object.keys(matched).length > 0) {
            const setCopy = { ...(cs as Record<string, unknown>) };
            setCopy.commands = matched;
            fcs[setId] = setCopy;
          }
        }
        if (doc.components) filtered.components = doc.components;
        process.stdout.write(JSON.stringify(filtered, null, 2) + "\n");
      }
      process.exit(0);
    });
  return program;
}
